// src/sidebars/SidebarVisitor.tsx
import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../shared/contexts/AuthContext";
import api from "../../shared/api/axios";
import HighLogo from "../HighLogo";
import logo from "../../assets/logo.png";
import { Bell } from "lucide-react";
import NotificationSign from "../components/NotificationSign";
import { useTranslation } from "react-i18next";

type SidebarVisitorProps = {
  onClose?: () => void;
  target?: { _id: string; profilename?: string | null; picture?: string | null } | null;
  onBlocked?: (userId: string) => void;
  onRequestReport?: (userId: string) => void;
  className?: string;
};

const GOLD = "#D4AF37";
const TEXT_DEFAULT = "#ccc";
const BG = "#000";
const BORDER = "#222";
const RED = "#D24444";

const SidebarVisitor: React.FC<SidebarVisitorProps> = ({
  onClose,
  target = null,
  onBlocked,
  className,
  onRequestReport,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userId: userIdFromParams } = useParams<{ userId: string }>();
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const [showProfileSidebar, setShowProfileSidebar] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [isFollowing, setIsFollowing] = React.useState(false);
  const [iBlock, setIBlock] = React.useState(false);
  const [theyBlock, setTheyBlock] = React.useState(false);

  const myId = user?._id || "";

  // Helpers for Profile Visit
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";
  const getAvatarUrl = (p?: string) =>
    p ? (p.startsWith("http") ? p : `${baseUrl}${p}`) : "";

  type PublicVisitor = { profilename?: string; picture?: string };
  const [visitor, setVisitor] = React.useState<PublicVisitor | null>(null);
  const visitorAvatarUrl = getAvatarUrl(visitor?.picture);

  const routeUserId =
    userIdFromParams ||
    pathname.match(/\/visitor-(?:profile)\/([^/]+)/)?.[1] ||
    "";
  const targetId = target?._id || routeUserId || "";
  const isSelf = !!myId && !!targetId && myId === targetId;

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (!routeUserId) return;
      try {
        const { data } = await api.get(`/users/${routeUserId}`);

        const profilename =
          data?.profilename ??
          data?.user?.profilename ??
          data?.displayName ??
          data?.username ??
          data?.name ??
          "";
        const picture = data?.picture ?? data?.user?.picture ?? "";

        if (mounted) setVisitor({ profilename, picture });
      } catch (e) {
        console.error("Failed to load visitor:", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [routeUserId]);

  // Active states for quick actions
  const isPostings =
    !!routeUserId && pathname.startsWith(`/visitor-profile/${routeUserId}`);
  const isChat = !!routeUserId && pathname.startsWith(`/chat/${routeUserId}`);

  const goChat = () => {
    if (!routeUserId) return;
    navigate("/chat", {
      state: {
        toUserId: routeUserId,
        toUserName: visitor?.profilename || "",
        toUserPicture: visitor?.picture || "",
      },
    });
    onClose?.();
  };

  // Fetch follow + block state (same as ProfileActions)
  React.useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!targetId || !myId || isSelf) {
        setLoading(false);
        setIsFollowing(false);
        setIBlock(false);
        setTheyBlock(false);
        return;
      }
      try {
        setLoading(true);
        const [stats, block] = await Promise.all([
          api
            .get(`/follows/${encodeURIComponent(targetId)}/stats`)
            .catch(() => ({ data: { isFollowing: false } })),
          api
            .get(`/block-report/block/check/${encodeURIComponent(targetId)}`)
            .catch(() => ({ data: { iBlock: false, theyBlock: false } })),
        ]);
        if (cancelled) return;
        setIsFollowing(!!stats?.data?.isFollowing);
        setIBlock(!!block?.data?.iBlock);
        setTheyBlock(!!block?.data?.theyBlock);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [targetId, myId, isSelf]);

  if (!targetId || isSelf) return null;

  const followDisabled = isSelf || iBlock || theyBlock || loading;

  const toggleFollow = async () => {
    if (!targetId || isSelf) return;
    try {
      if (isFollowing) {
        await api.delete(`/follows/${encodeURIComponent(targetId)}`);
        setIsFollowing(false);
      } else {
        await api.post(`/follows/${encodeURIComponent(targetId)}`);
        setIsFollowing(true);
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.msg ||
        e?.message ||
        t("sidebarVisitor.alerts.actionFailed");
      alert(msg);
    }
  };

  const block = async () => {
    if (!targetId || isSelf) return;
    const name = target?.profilename
      ? `@${target.profilename}`
      : t("sidebarVisitor.labels.thisUser");
    const ok = window.confirm(
      t("sidebarVisitor.confirm.block", { name })
    );
    if (!ok) return;
    try {
      await api.post(`/block-report/block/${encodeURIComponent(targetId)}`);
      setIBlock(true);
      onBlocked?.(targetId);
      alert(t("sidebarVisitor.alerts.blockSuccess"));
    } catch (e: any) {
      const msg =
        e?.response?.data?.msg ||
        e?.message ||
        t("sidebarVisitor.alerts.blockError");
      alert(msg);
    }
  };

  const unblock = async () => {
    if (!targetId || isSelf) return;
    try {
      await api.post(`/block-report/unblock/${encodeURIComponent(targetId)}`);
      setIBlock(false);
      alert(t("sidebarVisitor.alerts.unblockSuccess"));
    } catch (e: any) {
      const msg =
        e?.response?.data?.msg ||
        e?.message ||
        t("sidebarVisitor.alerts.unblockError");
      alert(msg);
    }
  };

  const blockAndReport = async () => {
    if (!targetId || isSelf) return;
    try {
      if (!iBlock) {
        await api.post(`/block-report/block/${encodeURIComponent(targetId)}`);
        setIBlock(true);
        onBlocked?.(targetId);
      }
      if (onRequestReport) {
        onRequestReport(targetId);
      } else {
        navigate(
          `/report?targetType=user&targetId=${encodeURIComponent(targetId)}`
        );
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.msg ||
        e?.message ||
        t("sidebarVisitor.alerts.blockError");
      alert(msg);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');

    onClose?.();
  };

  return (
    <aside style={wrap} className={className}>
      {/* Gold & danger hover styles */}
      <style>
        {`
        .gold-hover:hover,
        .gold-hover:focus { border-color: #D4AF37 !important; outline: none; }
        .gold-hover:disabled { cursor: not-allowed; opacity: 0.6; }

        .danger-hover:hover,
        .danger-hover:focus { border-color: #D24444 !important; outline: none; }
      `}
      </style>

      {/* Slide-in profile settings drawer (currently just overlay) */}
      {showProfileSidebar && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            width: "100%",
            backgroundColor: "#000",
            zIndex: 3000,
          }}
        />
      )}

 {/* Header: logo + notification (independent positioning) */}
<div
  style={{
    position: "relative",
    display: "flex",
    alignItems: "center",
    padding: "12px 20px",
    marginBottom: "-20px",
    marginTop: "1px",
    color: "#fff",
    minHeight: 64,
  }}
>
  <div
    style={{
      marginLeft: "-25%",
      marginTop: "9%",
      marginBottom: "13%",
    }}
  >
    <HighLogo
      src={logo}
      style={{ width: "70%" }}
      padding={0}
      onClick={() => navigate("/gallery")}
    />
  </div>

  <div
    style={{
      position: "absolute",
      top: 34,
      right: 30,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10,
    }}
  >
    <NotificationSign
      to="/notifications"
      icon={<Bell size={21} color="#ff8c00" strokeWidth={2.2} />}
    />
  </div>
</div>


      {/* Visitor header above Quick Actions (for Postings) */}
      <h3 style={postMenuHeader}>{t("sidebarVisitor.headers.postsBy")}</h3>

      {isPostings && visitor && (
        <div style={visitorHdrWrap}>
          <div style={visitorHdrAvatar}>
            {visitorAvatarUrl ? (
              <img
                src={visitorAvatarUrl}
                alt="avatar"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#0b0b0b",
                }}
              >
                👻
              </div>
            )}
          </div>
          <div style={visitorHdrText}>
            <span style={{ color: "#e6e9f3", fontWeight: 600 }}>
              {visitor.profilename ||
                t("sidebarVisitor.labels.userFallback")}
            </span>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <hr style={hr} />
      <h3 style={{...menuHeader, marginBottom: 16}}>{t("sidebarVisitor.headers.chat")}</h3>

      <nav style={{ marginBottom: 0 }}>
        <button
          className="gold-hover"
          style={{ ...menuBtn, ...(isChat ? menuBtnActive : {}) }}
          onClick={goChat}
          disabled={!routeUserId}
          title={t("sidebarVisitor.tooltips.chatWith", {
            name:
              visitor?.profilename ||
              t("sidebarVisitor.labels.userFallback"),
          })}
        >
          💬{" "}
          <span
            style={{
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginLeft: 8,
              fontSize: 12,
            }}
          >
            {visitor?.profilename
              ? t("sidebarVisitor.buttons.chatWithName", {
                  name: visitor.profilename,
                })
              : t("sidebarVisitor.buttons.chatShort")}
          </span>
        </button>
      </nav>

      {/* Main Navigation */}
      <hr style={hr} />
      <h3 style={{...menuHeader, marginBottom: 16}}>{t("sidebarVisitor.headers.mainNav")}</h3>

      <nav>
        <button
          className="gold-hover"
          onClick={() => {
            navigate("/gallery");
            onClose?.();
          }}
          style={menuBtn}
        >
          {t("sidebarVisitor.buttons.backToGallery")}
        </button>

        <button
          className="gold-hover"
          onClick={() => {
            navigate("/following");
            onClose?.();
          }}
          style={menuBtn}
        >
          🌊 {t("sidebarVisitor.buttons.following")}
        </button>

        <button
          className="gold-hover"
          onClick={() => {
            navigate("/following-list");
            onClose?.();
          }}
          style={menuBtn}
        >
          📚 {t("sidebarVisitor.buttons.favorites")}
        </button>

        <button
          className="gold-hover"
          onClick={() => {
            navigate("/followers-list");
            onClose?.();
          }}
          style={menuBtn}
        >
          📚 {t("sidebarVisitor.buttons.followers")}
        </button>
      </nav>

      {/* Profile Actions */}
      <hr style={hr} />
      <h3 style={{...menuHeader, marginBottom: 16}}>{t("sidebarVisitor.headers.profileActions")}</h3>

      {/* Follow / Unfollow */}
      <button
        type="button"
        className="gold-hover"
        style={{
          ...(isFollowing ? menuBtnEmphasis : menuBtn),
          ...(followDisabled
            ? { opacity: 0.5, cursor: "not-allowed" }
            : null),
        }}
        disabled={followDisabled}
        onClick={toggleFollow}
      >
        {isFollowing
          ? t("sidebarVisitor.buttons.unfollow")
          : t("sidebarVisitor.buttons.follow")}
      </button>

      {/* Block / Unblock */}
      {iBlock ? (
        <button
          type="button"
          className="gold-hover"
          style={menuBtn}
          onClick={unblock}
        >
          {t("sidebarVisitor.buttons.unblock")}
        </button>
      ) : (
        <button
          type="button"
          className="gold-hover"
          style={menuBtn}
          onClick={block}
        >
          {t("sidebarVisitor.buttons.block")}
        </button>
      )}

      {/* Block & Report */}
      {!iBlock && (
        <button
          type="button"
          className="gold-hover"
          style={menuBtn}
          onClick={blockAndReport}
        >
          {t("sidebarVisitor.buttons.blockAndReport")}
        </button>
      )}

      {/* Divider + Logout at the very bottom, like SidebarMain/Settings */}
      <hr style={{ ...hr, marginTop: "auto" }} />

      <button
        type="button"
        className="danger-hover"
        style={dangerBtn}
        onClick={handleLogout}
      >
        📕 {t("mainSidebar.buttons.logout")}
      </button>
    </aside>
  );
};

/* ---------- UI (styles) ---------- */

const wrap: React.CSSProperties = {
  width: '230px',
  background:
    'linear-gradient(145deg, rgba(5, 9, 20, 0.95), rgba(4, 7, 15, 0.95))',
  color: '#bbb',
  padding: '20px',
  height: 'calc(100vh - 50px)',
  boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
  position: 'fixed',
  left: 0,
  marginTop: '-2%',
  overflow: 'hidden',
};

const postMenuHeader: React.CSSProperties = {
  color: '#bbb',
  fontWeight: 700,
  marginBottom: 10,
  fontSize: 13,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  marginTop: "20px",
  marginLeft: 0,
};

const menuHeader: React.CSSProperties = {
  color: '#bbb',
  fontWeight: 700,
  marginBottom: 10,
  fontSize: 13,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  marginTop: "20px",
};

const hr: React.CSSProperties = {
  border: 0,
  borderTop: `1px solid ${BORDER}`,
  margin: '14px 0',
};

const baseBtn: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "8px 10px",
  marginBottom: "8px",
  backgroundColor: BG,
  color: TEXT_DEFAULT,
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 500,
  textAlign: "left",
  fontSize: 14,
  transition: "border-color 120ms ease, background-color 120ms ease, color 120ms ease",
};
// Default button (black bg)
const menuBtn: React.CSSProperties = { ...baseBtn };

// Active/selected state — gold background AND gold border
const menuBtnActive: React.CSSProperties = {
  backgroundColor: GOLD,
  color: "#000",
  border: `1px solid ${GOLD}`,
};

// Emphasis version (e.g., Unfollow) — gold border without fill
const menuBtnEmphasis: React.CSSProperties = {
  ...baseBtn,
  border: `1px solid ${GOLD}`,
};

const dangerBtn: React.CSSProperties = {
  ...baseBtn,
  border: `1px solid ${RED}`,
  marginTop: 25,
};

const visitorHdrWrap: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: "6px 0 6px",
};

const visitorHdrAvatar: React.CSSProperties = {
  width: 35,
  height: 35,
  borderRadius: 10,
  overflow: "hidden",
  border: `1px solid ${BORDER}`,
  background: "#0a0a0a",
  flexShrink: 0,
};

const visitorHdrText: React.CSSProperties = {
  fontSize: 11,
  lineHeight: 1.2,
  color: "#e6e9f3",
};

export default SidebarVisitor;
